/**
 * Employee form avatar: standard initials reset, image upload area, crop/filter editor.
 */

import Cropper from 'cropperjs';
import { DirectUpload } from '@rails/activestorage';
import { generateAvatarCircleFill, applyAvatarPhoto, clearAvatarPhoto } from './redfish';

const FILTER_CSS = {
  none: 'none',
  grayscale: 'grayscale(1)',
  sepia: 'sepia(1)',
  bw: 'grayscale(1) contrast(1.35)'
};

function csrf_token() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function wrap_for(event_target) {
  return event_target && event_target.closest && event_target.closest('.js-employee-form-avatar-wrap');
}

function employee_names() {
  const nachname = document.getElementById('employee_nachname');
  const vorname = document.getElementById('employee_vorname');
  return {
    nachname: nachname ? nachname.value : '',
    vorname: vorname ? vorname.value : ''
  };
}

function generated_style(wrap) {
  const select = document.querySelector('.js-employee-avatar-style');
  if (select && select.value) return select.value;
  return (wrap && wrap.getAttribute('data-avatar-style')) || 'soft_radial';
}

function fill_generated(wrap) {
  if (!wrap) return;
  const names = employee_names();
  const style = generated_style(wrap);
  wrap.querySelectorAll('.js-employee-form-avatar').forEach((el) => {
    clearAvatarPhoto(el);
    el.setAttribute('data-nachname', names.nachname);
    el.setAttribute('data-vorname', names.vorname);
    el.setAttribute('data-avatar-style', style);
    el.setAttribute('data-avatar-kind', 'employee');
    generateAvatarCircleFill(names.nachname, names.vorname, el, style, 'employee');
  });
}

function set_slot(wrap, mode) {
  const stage = wrap.querySelector('.js-employee-avatar-stage');
  const upload_wrap = wrap.querySelector('.js-employee-avatar-upload-wrap');
  const editor = wrap.querySelector('.js-employee-avatar-editor');
  if (stage) stage.hidden = mode !== 'stage';
  if (upload_wrap) upload_wrap.hidden = mode !== 'upload';
  if (editor) editor.hidden = mode !== 'editor';
}

function destroy_cropper(wrap) {
  if (wrap.employeeAvatarCropper) {
    wrap.employeeAvatarCropper.destroy();
    wrap.employeeAvatarCropper = null;
  }
  wrap.employeeAvatarStarting = false;
  wrap.employeeAvatarFilter = 'none';
  if (wrap.employeeAvatarObjectUrl) {
    URL.revokeObjectURL(wrap.employeeAvatarObjectUrl);
    wrap.employeeAvatarObjectUrl = null;
  }
  const img = wrap.querySelector('.js-employee-avatar-cropper-img');
  if (img) {
    img.onload = null;
    img.src = '';
    img.style.filter = 'none';
  }
}

function hide_editor_and_upload(wrap) {
  destroy_cropper(wrap);
  set_slot(wrap, 'stage');
}

function show_upload_area(wrap) {
  destroy_cropper(wrap);
  set_slot(wrap, 'upload');
}

function apply_pixel_filter(canvas, filter_name) {
  if (!filter_name || filter_name === 'none') return canvas;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const image_data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = image_data.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    if (filter_name === 'grayscale' || filter_name === 'bw') {
      let y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      if (filter_name === 'bw') y = Math.min(255, Math.max(0, (y - 128) * 1.35 + 128));
      pixels[i] = y;
      pixels[i + 1] = y;
      pixels[i + 2] = y;
    } else if (filter_name === 'sepia') {
      pixels[i] = Math.min(255, 0.393 * r + 0.769 * g + 0.189 * b);
      pixels[i + 1] = Math.min(255, 0.349 * r + 0.686 * g + 0.168 * b);
      pixels[i + 2] = Math.min(255, 0.272 * r + 0.534 * g + 0.131 * b);
    }
  }
  ctx.putImageData(image_data, 0, 0);
  return canvas;
}

function apply_filter_to_cropper(wrap, filter_name) {
  wrap.employeeAvatarFilter = filter_name || 'none';
  const css = FILTER_CSS[wrap.employeeAvatarFilter] || 'none';
  const cropper = wrap.employeeAvatarCropper;
  const root = wrap.querySelector('.cropper-container');
  if (root) {
    root.querySelectorAll('.cropper-canvas, .cropper-crop-box').forEach((el) => {
      el.style.filter = css;
    });
  }
  if (cropper && cropper.image) cropper.image.style.filter = css;
}

function start_editor(wrap, file) {
  const editor = wrap.querySelector('.js-employee-avatar-editor');
  const img = wrap.querySelector('.js-employee-avatar-cropper-img');
  if (!editor || !img || !file || !file.type || file.type.indexOf('image/') !== 0) return;
  if (wrap.employeeAvatarStarting) return;

  destroy_cropper(wrap);
  wrap.employeeAvatarStarting = true;
  wrap.employeeAvatarObjectUrl = URL.createObjectURL(file);
  wrap.employeeAvatarFilter = 'none';
  set_slot(wrap, 'editor');

  const init_cropper = function () {
    if (wrap.employeeAvatarCropper) return;
    wrap.employeeAvatarCropper = new Cropper(img, {
      aspectRatio: 1,
      viewMode: 1,
      dragMode: 'move',
      movable: true,
      zoomable: true,
      zoomOnWheel: true,
      autoCropArea: 1,
      background: false,
      modal: true,
      guides: false,
      center: false,
      highlight: false,
      cropBoxMovable: false,
      cropBoxResizable: false,
      toggleDragModeOnDblclick: false,
      wheelZoomRatio: 0.1,
      ready() {
        wrap.employeeAvatarStarting = false;
        this.setDragMode('move');
        apply_filter_to_cropper(wrap, 'none');
      }
    });
  };

  img.onload = init_cropper;
  img.src = wrap.employeeAvatarObjectUrl;
  if (img.complete && img.naturalWidth) init_cropper();
}

function visible_avatar_canvas(wrap, cropper) {
  const wrap_el = wrap.querySelector('.employee-avatar-cropper-wrap');
  const img = wrap.querySelector('.cropper-canvas img');
  const fallback = function () {
    return cropper.getCroppedCanvas({
      width: 400,
      height: 400,
      fillColor: '#ffffff',
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    });
  };
  if (!wrap_el || !img || !img.naturalWidth) return fallback();

  const wrap_rect = wrap_el.getBoundingClientRect();
  const img_rect = img.getBoundingClientRect();
  if (wrap_rect.width < 1 || img_rect.width < 1 || img_rect.height < 1) return fallback();

  const sx = (wrap_rect.left - img_rect.left) * (img.naturalWidth / img_rect.width);
  const sy = (wrap_rect.top - img_rect.top) * (img.naturalHeight / img_rect.height);
  const sw = wrap_rect.width * (img.naturalWidth / img_rect.width);
  const sh = wrap_rect.height * (img.naturalHeight / img_rect.height);
  if (!(sw > 0) || !(sh > 0)) return fallback();

  const out = document.createElement('canvas');
  out.width = 400;
  out.height = 400;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 400, 400);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  try {
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 400, 400);
  } catch (error) {
    return fallback();
  }
  return out;
}

function cropped_blob(wrap) {
  return new Promise((resolve, reject) => {
    const cropper = wrap.employeeAvatarCropper;
    if (!cropper) return reject(new Error('Kein Bild zum Zuschneiden'));
    const canvas = visible_avatar_canvas(wrap, cropper);
    if (!canvas) return reject(new Error('Zuschneiden fehlgeschlagen'));
    apply_pixel_filter(canvas, wrap.employeeAvatarFilter);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Bild konnte nicht erzeugt werden'));
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
}

function direct_upload_blob(file, url) {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, url);
    upload.create((error, blob) => {
      if (error) return reject(error);
      resolve(blob);
    });
  });
}

function post_json(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrf_token()
    },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  }).then((response) => response.json().then((data) => {
    if (!response.ok || data.ok === false) {
      throw new Error(data.error || 'Speichern fehlgeschlagen');
    }
    return data;
  }));
}

function show_form_photo(wrap, url) {
  hide_editor_and_upload(wrap);
  wrap.querySelectorAll('.js-employee-form-avatar').forEach((el) => {
    applyAvatarPhoto(el, url);
  });
  const header = document.querySelector('.user_avatar_circle');
  const employee_id = wrap.getAttribute('data-employee-id');
  if (header && header.getAttribute('data-employee-id') === employee_id) {
    applyAvatarPhoto(header, url);
  }
}

async function reset_to_generated(wrap) {
  const reset_url = wrap.getAttribute('data-reset-url');
  if (reset_url) {
    await post_json(reset_url, {});
  }
  hide_editor_and_upload(wrap);
  fill_generated(wrap);
  const header = document.querySelector('.user_avatar_circle');
  const employee_id = wrap.getAttribute('data-employee-id');
  if (header && header.getAttribute('data-employee-id') === employee_id) {
    const names = employee_names();
    clearAvatarPhoto(header);
    generateAvatarCircleFill(names.nachname, names.vorname, header, generated_style(wrap), 'employee');
  }
}

async function commit_edited_image(wrap) {
  const ok_button = wrap.querySelector('.js-employee-avatar-editor-ok');
  if (ok_button) ok_button.disabled = true;
  try {
    const blob = await cropped_blob(wrap);
    const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
    const direct_url = wrap.getAttribute('data-direct-upload-url');
    const uploaded = await direct_upload_blob(file, direct_url);
    const data = await post_json(wrap.getAttribute('data-upload-url'), { signed_id: uploaded.signed_id });
    const cache_bust_url = data.url + (data.url.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
    show_form_photo(wrap, cache_bust_url);
    if (data.warning && window.alert) window.alert(data.warning);
  } catch (error) {
    if (window.alert) window.alert(error.message || 'Avatar konnte nicht gespeichert werden');
  } finally {
    if (ok_button) ok_button.disabled = false;
  }
}

function on_document_click(event) {
  const wrap = wrap_for(event.target);
  if (!wrap) return;
  if (wrap.getAttribute('data-can-edit') !== 'true' || wrap.getAttribute('data-persisted') !== 'true') return;

  if (event.target.closest('.js-employee-avatar-reset')) {
    event.preventDefault();
    reset_to_generated(wrap);
    return;
  }

  if (event.target.closest('.js-employee-avatar-pick')) {
    event.preventDefault();
    show_upload_area(wrap);
    return;
  }

  if (event.target.closest('.js-employee-avatar-dropzone') && !event.target.closest('.js-employee-avatar-file')) {
    const input = wrap.querySelector('.js-employee-avatar-file');
    if (input) input.click();
    return;
  }

  if (event.target.closest('.js-employee-avatar-filter')) {
    event.preventDefault();
    apply_filter_to_cropper(wrap, event.target.closest('.js-employee-avatar-filter').getAttribute('data-filter'));
    return;
  }

  if (event.target.closest('.js-employee-avatar-editor-cancel')) {
    event.preventDefault();
    hide_editor_and_upload(wrap);
    return;
  }

  if (event.target.closest('.js-employee-avatar-editor-ok')) {
    event.preventDefault();
    commit_edited_image(wrap);
  }
}

function on_file_change(event) {
  const input = event.target && event.target.closest && event.target.closest('.js-employee-avatar-file');
  if (!input || !input.files || !input.files[0]) return;
  const wrap = wrap_for(input);
  if (!wrap) return;
  start_editor(wrap, input.files[0]);
  input.value = '';
}

function on_drag(event) {
  const dropzone = event.target && event.target.closest && event.target.closest('.js-employee-avatar-dropzone');
  if (!dropzone) return;
  event.preventDefault();
  if (event.type === 'dragover' || event.type === 'dragenter') {
    dropzone.classList.add('dragover');
  } else if (event.type === 'dragleave' || event.type === 'drop') {
    dropzone.classList.remove('dragover');
  }
  if (event.type === 'drop' && event.dataTransfer && event.dataTransfer.files[0]) {
    const wrap = wrap_for(dropzone);
    if (wrap) start_editor(wrap, event.dataTransfer.files[0]);
  }
}

export function initEmployeeAvatarForm() {
  if (document.documentElement.dataset.employeeAvatarFormReady === 'true') return;
  document.documentElement.dataset.employeeAvatarFormReady = 'true';

  document.addEventListener('click', on_document_click);
  document.addEventListener('change', on_file_change);
  document.addEventListener('dragover', on_drag);
  document.addEventListener('dragenter', on_drag);
  document.addEventListener('dragleave', on_drag);
  document.addEventListener('drop', on_drag);

  document.addEventListener('blur', (event) => {
    if (!event.target || !event.target.matches) return;
    if (!event.target.matches('#employee_nachname, #employee_vorname')) return;
    const wrap = document.querySelector('.js-employee-form-avatar-wrap');
    if (!wrap) return;
    const stage_avatar = wrap.querySelector('.js-employee-form-avatar');
    if (stage_avatar && stage_avatar.classList.contains('is-photo')) return;
    fill_generated(wrap);
  }, true);
}
